import { Factory, trait } from 'miragejs';
import { faker } from '@faker-js/faker';
import { FlowType } from 'garaje/utils/enums';
import { v4 as uuid } from 'uuid';

export default Factory.extend({
  name: () => faker.person.jobTitle(),
  additionalGuests: false,
  enabled: true,
  idCheck: false,
  position(i) {
    return i + 1;
  },
  visitorSurveyEnabled: false,
  __flowUUID: () => uuid(),

  asEmployeeRegistration: trait({
    name: 'Employee registration',
    type: FlowType.EMPLOYEE_SCREENING,
    afterCreate(flow, server) {
      server.create('summary-page', { message: null, flow });
      const employeeScreeningFlow = server.schema.employeeScreeningFlows.findBy({ __flowUUID: flow.__flowUUID });
      if (!employeeScreeningFlow) {
        server.create('employee-screening-flow', {
          id: flow.id,
          name: flow.name,
          enabled: flow.enabled,
          location: flow.location,
          locations: flow.locations,
          __flowUUID: flow.__flowUUID,
        });
      } else {
        if (employeeScreeningFlow.id !== flow.id) {
          const originalId = employeeScreeningFlow.id;
          employeeScreeningFlow.update({ id: flow.id });

          // updating the ID can create another model with the new ID; destroy the original so we don't end up with more records than we expect
          server.schema.employeeScreeningFlows.find(originalId)?.destroy();
        }
      }
    },
  }),

  // 'withES' is deprectated, use 'asEmployeeRegistration' in new code.
  withES: trait({
    type: FlowType.EMPLOYEE_SCREENING,
    afterCreate(flow, server) {
      server.create('summary-page', { message: null, flow });
    },
  }),

  withQuestions: trait({
    afterCreate(flow, server) {
      server.create('sign-in-field-page', 'withQuestions', { flow });
    },
  }),

  withRules: trait({
    afterCreate(flow, server) {
      const page = server.create('sign-in-field-page', 'withQuestions', { flow });

      const signInFieldAction = server.create('sign-in-field-action', {
        signInFieldId: page.signInFieldIds[0],
        action: 'alert',
        matchOperator: 'is',
        signInFieldValue: 1,
      });

      server.createList('sign-in-field-action-rule-group', 3, {
        flow,
        signInFieldActions: [signInFieldAction],
        actions: ['alert'],
      });
    },
  }),

  withOneDenyEntryRule: trait({
    afterCreate(flow, server) {
      const page = server.create('sign-in-field-page', 'withQuestions', { flow });

      const signInFieldAction = server.create('sign-in-field-action', {
        signInFieldId: page.signInFieldIds[0],
        action: 'deny_entry',
        matchOperator: 'is',
        signInFieldValue: 1,
      });

      server.create('sign-in-field-action-rule-group', {
        flow,
        signInFieldActions: [signInFieldAction],
        actions: ['deny_entry'],
      });
    },
  }),

  withOneAlertRule: trait({
    afterCreate(flow, server) {
      const page = server.create('sign-in-field-page', 'withQuestions', { flow });

      const signInFieldAction = server.create('sign-in-field-action', {
        signInFieldId: page.signInFieldIds[0],
        action: 'deny_entry', // deny_entry is the correct option for now there is no `alert`
        matchOperator: 'is',
        signInFieldValue: 1,
      });

      server.create('sign-in-field-action-rule-group', 'withOneContact', {
        flow,
        signInFieldActions: [signInFieldAction],
        actions: ['alert'],
      });
    },
  }),

  asPropertyWalkUp: trait({
    name: 'Property walk-up',
    type: FlowType.PROPERTY_WALKUP,
  }),

  asPropertyWithQRCode: trait({
    enabled: true,
    name: 'I have a QR code',
    type: FlowType.PROPERTY,

    afterCreate(flow, server) {
      server.create('summary-page', {
        flow,
        kind: 'message',
        message: faker.lorem.paragraph(),
      });
    },
  }),
  asPropertyWithoutQRCode: trait({
    enabled: true,
    name: "I don't have a QR code",
    type: FlowType.PROPERTY,

    afterCreate(flow, server) {
      server.create('summary-page', {
        flow,
        kind: 'message',
        message: faker.lorem.paragraph(),
      });
    },
  }),

  asGlobalChild: trait({
    afterCreate(flow) {
      const globalFlow = flow.globalFlow;
      if (globalFlow) {
        flow.update({
          type: FlowType.GLOBAL_CHILD,

          // copy attributes from global-flow to flow (not comprehensive; add additional ones as needed)
          name: globalFlow.name,
          inviteApprovalContacts: globalFlow.inviteApprovalContacts,
          inviteApprovalsEnabled: globalFlow.inviteApprovalsEnabled,
          signInFieldPage: globalFlow.signInFieldPage,
        });
        globalFlow.update({
          locations: [...globalFlow.locations.models, flow.location],
        });
      }
    },
  }),
});
